import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { Subject, combineLatest } from 'rxjs';
import { filter, tap, takeUntil, switchMap, take } from 'rxjs/operators';

import { VIRoutingMap } from '@web/app/routing/routes';

import { TranslateHelperService } from '@common/modules/translation/services/translate-helper.service';
import { FocusManagerService } from '@common/modules/accessibility/focus-manager.service';
import { SideMenuOption } from '@common/modules/core/services/interfaces';
import { EventCategory, TrackService } from '@common/modules/core/services/track';
import { SharedModule } from '@common/modules/shared/shared.module';
import { FocusableComponent } from '@common/modules/accessibility/focusable/focusable.component';

import { EdgeExtensionsStoreService } from '../../../core/services/edge-extensions-store.service';
import { resources } from './resources';
import { CoreStoreService } from '../../../core/services/core-store.service';
import { IExtensionMenuItem } from './interfaces';

@Component({
  selector: 'vi-edge-extensions-sub-menu',
  templateUrl: './edge-extensions-sub-menu.component.html',
  styleUrls: ['./edge-extensions-sub-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SharedModule]
})
export class EdgeExtensionsSubMenuComponent extends FocusableComponent implements OnInit, OnDestroy {
  public menuItems: IExtensionMenuItem[];
  public resources = resources;
  public hasSelectedEdgeExtension: boolean;

  private readonly CLOUD_ITEM_CLASS = 'cloud';
  private readonly MULTI_EXTENSIONS_CLASS = 'multi-extensions';
  private readonly destroy$ = new Subject<void>();

  private isMediaSideMenuOptionSelected: boolean;

  constructor(
    public edgeExtensionsStore: EdgeExtensionsStoreService,
    public coreStore: CoreStoreService,
    private translate: TranslateHelperService,
    private cdr: ChangeDetectorRef,
    private focusManager: FocusManagerService,
    private trackService: TrackService,
    private router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.translate.translateResourcesInstant(resources);
    this.setupDataStreams();
    this.setupNavigationHandler();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleSelectItem(item: IExtensionMenuItem): void {
    if (!item.value) {
      // Always navigate to media gallery for "My Cloud" item
      if (this.hasSelectedEdgeExtension) {
        this.edgeExtensionsStore.clearSelectedEdgeExtension();
      }
      this.trackService.track('extension_submenu.my_cloud.clicked', {
        category: EventCategory.EDGE
      });
      this.coreStore.navigate([`${VIRoutingMap.mediaGallery.path}`], null, null, true);
    } else if (item.value === this.MULTI_EXTENSIONS_CLASS) {
      this.trackService.track('extension_submenu.arc_extensions.clicked', {
        category: EventCategory.EDGE
      });
      this.coreStore.navigate([`${VIRoutingMap.edgeExtensions.path}`], null, null, true);
    }
  }

  private setupDataStreams(): void {
    combineLatest([this.coreStore.selectSideMenuSelectedOption(), this.edgeExtensionsStore.selectedEdgeExtensionId$])
      .pipe(
        takeUntil(this.destroy$),
        tap(([sideMenuOption, selectedEdgeExtensionId]) => {
          this.isMediaSideMenuOptionSelected = sideMenuOption === SideMenuOption.Media;
          this.hasSelectedEdgeExtension = !!selectedEdgeExtensionId;
        })
      )
      .subscribe(() => {
        this.initMenuItems();
      });
  }

  private setupNavigationHandler(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        switchMap(() => this.edgeExtensionsStore.selectedEdgeExtensionId$.pipe(take(1)))
      )
      .subscribe(selectedEdgeExtensionId => {
        if (this.menuItems) {
          this.hasSelectedEdgeExtension = !!selectedEdgeExtensionId;
          this.initMenuItems();
          this.cdr.detectChanges();
          this.focusEdgeExtensionItem();
        }
      });
  }

  private initMenuItems(): void {
    const currentUrl = this.router.url;

    const cloudItem: IExtensionMenuItem = {
      value: '',
      selected: this.isMediaSideMenuOptionSelected && currentUrl.includes(VIRoutingMap.mediaGallery.path) && !this.hasSelectedEdgeExtension,
      text: resources.MyCloud,
      class: this.CLOUD_ITEM_CLASS,
      isExtension: false
    };

    const multiExtensionsItem: IExtensionMenuItem = {
      value: this.MULTI_EXTENSIONS_CLASS,
      selected: this.isMediaSideMenuOptionSelected && this.router.url.includes(VIRoutingMap.edgeExtensions.path),
      text: resources.EdgeExtensionsPageTitle,
      class: this.MULTI_EXTENSIONS_CLASS,
      isExtension: false
    };

    this.menuItems = [cloudItem, multiExtensionsItem];
  }

  private focusEdgeExtensionItem(): void {
    const menuItem = this.menuItems.find(item => item.selected);
    if (menuItem && this.accessibilityMode) {
      this.focusManager.focusByQuery(`.${menuItem.class}`, this.cdr);
    }
  }
}
